import { useState } from 'react';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { commonSelectStyles } from '../Select/ReactSelect';

export type MultiValueOption = { value: string; label: string };

type MultiValueInputProps = {
  value: string[];
  onChange: (values: string[]) => void;
  suggestions?: MultiValueOption[];
  disabled?: boolean;
  disableSpaces?: boolean;
  placeholder?: string;
};

export const multiValueSelectStyles: StylesConfig<MultiValueOption, true> = {
  ...(commonSelectStyles as StylesConfig<MultiValueOption, true>),
  control: (styles, state) => ({
    ...(commonSelectStyles.control?.(styles, state as never) as object),
    minHeight: '2.75rem',
    height: 'auto',
  }),
  valueContainer: (styles) => ({
    ...styles,
    flexWrap: 'wrap' as const,
    padding: '0.25rem 0.5rem',
  }),
  multiValue: (styles) => ({
    ...styles,
    margin: '0.125rem',
    borderRadius: 0,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: 'var(--color-slate-500)',
    ':hover': {
      backgroundColor: 'var(--color-slate-100)',
      color: 'var(--color-slate-700)',
    },
  }),
};

export function MultiValueInput({
  value,
  onChange,
  suggestions = [],
  disabled = false,
  disableSpaces = false,
  placeholder = 'Type and press Enter...',
}: MultiValueInputProps) {
  const [inputValue, setInputValue] = useState('');

  const sanitize = (input: string) => (
    disableSpaces ? input.replace(/\s/g, '') : input
  );

  const selectedOptions: MultiValueOption[] = value.map((v) => {
    const match = suggestions.find((s) => s.value === v);
    return match ?? { value: v, label: v };
  });

  return (
    <CreatableSelect<MultiValueOption, true>
      isMulti
      styles={multiValueSelectStyles}
      options={suggestions}
      value={selectedOptions}
      inputValue={inputValue}
      onInputChange={(val, meta) => {
        if (meta.action === 'input-change') {
          setInputValue(sanitize(val));
        } else {
          setInputValue(val);
        }
      }}
      onChange={(opts) => onChange((opts ?? []).map((o) => o.value))}
      placeholder={placeholder}
      isDisabled={disabled}
      formatCreateLabel={(input) => `Add "${input}"`}
    />
  );
}
